"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import Loader from "../Loader";

type Category = {
  id: string;
  name: string;
  image?: string;
  discount?: number;
  smDesc?: string;
  badge?: string;
  color?: string;
};

type ShopCategoryProps = {
  spacing?: string;
  categories?: Category[];
};

const ShopCategory = ({ spacing = "100px 0", categories = [] }: ShopCategoryProps) => {
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (!categories || categories.length === 0) return null;

  return (
    <>
      {loading && <Loader />}
      <section
        style={{
          padding: spacing,
          backgroundColor: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >

        {/* BACKGROUND CIRCLE */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: -200,
            width: "500px",
            height: "500px",
            background: "radial-gradient(circle, rgba(0,0,0,0.02) 0%, transparent 70%)",
            borderRadius: "50%",
            pointerEvents: "none",
          }}
        />

        <div style={{ maxWidth: "1400px", margin: "0 auto", padding: isMobile ? "0 16px" : "0 60px" }}>

          {/* SECTION HEADER */}
          <div style={{ textAlign: "center", marginBottom: isMobile ? "50px" : "80px", position: "relative", zIndex: 2 }}>
            <p
              style={{
                fontSize: isMobile ? "12px" : "13px",
                fontWeight: 700,
                letterSpacing: "2.5px",
                textTransform: "uppercase",
                color: "#999",
                marginBottom: "12px",
              }}
            >
              Explore Collections
            </p>

            <h2
              style={{
                fontSize: isMobile ? "40px" : "64px",
                fontWeight: 900,
                lineHeight: 1.2,
                color: "#000",
                marginBottom: "16px",
                letterSpacing: "-1px",
              }}
            >
              Browse by Category
            </h2>

            <p
              style={{
                fontSize: isMobile ? "15px" : "18px",
                color: "#666",
                lineHeight: 1.7,
                maxWidth: "600px",
                margin: "0 auto",
              }}
            >
              Discover our exclusive collection of premium products designed for style and performance.
            </p>
          </div>

          {/* CATEGORY GRID - UPDATED TO 5 CARDS DESKTOP */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(5, 1fr)",
              gap: isMobile ? "12px" : "32px",
              marginBottom: isMobile ? "40px" : "60px",
            }}
          >
            {categories.map((item) => (
              <Link
                key={item.id}
                href={`/shop?category=${item.id}`}
                onClick={() => setLoading(true)}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                  transition: "all 0.4s",
                  transform: hoveredCard === item.id ? "translateY(-12px)" : "translateY(0)",
                }}
              >
                <div
                  style={{
                    background: "#f9f9f9",
                    borderRadius: "20px",
                    padding: isMobile ? "20px 12px" : "40px 32px",
                    textAlign: "center",
                    position: "relative",
                    overflow: "hidden",
                    border: "1px solid #eee",
                    transition: "0.4s",
                    boxShadow:
                      hoveredCard === item.id
                        ? "0 20px 50px rgba(0,0,0,0.12)"
                        : "0 4px 15px rgba(0,0,0,0.05)",
                    cursor: "pointer",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >

                  {/* BACKGROUND ACCENT */}
                  <div
                    style={{
                      position: "absolute",
                      top: -50,
                      right: -50,
                      width: "200px",
                      height: "200px",
                      background: `radial-gradient(circle, ${item.color || "#ff6b6b"}15 0%, transparent 70%)`,
                      borderRadius: "50%",
                      transform: hoveredCard === item.id ? "scale(1.2)" : "scale(1)",
                      transition: "0.6s",
                      pointerEvents: "none",
                    }}
                  />

                  {/* BADGE */}
                  {item.badge && (
                    <div
                      style={{
                        position: "absolute",
                        top: "16px",
                        right: "16px",
                        padding: "6px 14px",
                        background: item.color || "#ff6b6b",
                        color: "#fff",
                        borderRadius: "50px",
                        fontSize: "11px",
                        fontWeight: 800,
                        letterSpacing: "1px",
                        textTransform: "uppercase",
                        zIndex: 2,
                      }}
                    >
                      {item.badge}
                    </div>
                  )}

                  {/* IMAGE */}
                  <div
                    style={{
                      width: "100%",
                      height: isMobile ? "90px" : "140px",
                      position: "relative",
                      marginBottom: isMobile ? "12px" : "24px",
                      borderRadius: "12px",
                      overflow: "hidden",
                      backgroundColor: "#f0f0f0",
                    }}
                  >
                    {item.image ? (
                      <Image
                        src={item.image}
                        alt={item.name}
                        fill
                        style={{
                          objectFit: "cover",
                          transition: "0.4s",
                          transform: hoveredCard === item.id ? "scale(1.12)" : "scale(1)",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? "60px" : "80px",
                        }}
                      >
                        📦
                      </div>
                    )}
                  </div>

                  {/* TEXT CONTENT */}
                  <h3
                    style={{
                      fontSize: isMobile ? "15px" : "22px",
                      fontWeight: 800,
                      color: "#000",
                      marginBottom: "6px",
                    }}
                  >
                    {item.name}
                  </h3>

                  <p
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#888",
                      marginBottom: "12px",
                      lineHeight: 1.4,
                    }}
                  >
                    {item.smDesc
                      ? item.smDesc.length > 35
                        ? item.smDesc.slice(0, 35) + "..."
                        : item.smDesc
                      : "Explore collection"}
                  </p>

                  {/* CTA */}
                  <div
                    style={{
                      color: item.color || "#ff6b6b",
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: 700,
                      opacity: hoveredCard === item.id ? 1 : 0.7,
                      transform: hoveredCard === item.id ? "translateX(5px)" : "translateX(0)",
                      transition: "0.3s",
                    }}
                  >
                    Shop Now →
                  </div>

                  {/* BOTTOM LINE */}
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "3px",
                      background: item.color || "#ff6b6b",
                      transform: hoveredCard === item.id ? "scaleX(1)" : "scaleX(0)",
                      transformOrigin: "center",
                      transition: "0.4s",
                    }}
                  />
                </div>
              </Link>
            ))}
          </div>

          {/* CTA */}
          <div style={{ textAlign: "center", paddingTop: "40px", borderTop: "1px solid #eee" }}>
            <p style={{ fontSize: "14px", color: "#888", marginBottom: "12px" }}>
              Can't find what you're looking for?
            </p>

            <br></br>

            <Link
              href="/shop"
              style={{
                padding: "14px 40px",
                background: "#000",
                color: "#fff",
                borderRadius: "8px",
                fontWeight: 700,
                textDecoration: "none",
                transition: "0.3s",
              }}
            >
              View All Products
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ShopCategory;
